import { z } from "zod";
import { actorSchema, contextStatusSchema, idSchema, instantSchema, sourceDateSchema } from "@amber/contracts";
import { evidenceRefSchema, msTupleSchema } from "./shared.js";

export const CORE_CORRECTION_KINDS = ["word", "turn_text", "speaker"] as const;
export const EXTENDED_CORRECTION_KINDS = [
  "profile_link",
  "timing",
  "turn_split",
  "turn_merge",
  "communication_split",
  "communication_merge",
  "date",
  "context_status",
  "supporting_evidence",
] as const;
export const CORRECTION_KINDS = [...CORE_CORRECTION_KINDS, ...EXTENDED_CORRECTION_KINDS] as const;
export const correctionKindSchema = z.enum(CORRECTION_KINDS);

export const CORRECTION_STATES = ["saved"] as const;
export const correctionStateSchema = z.enum(CORRECTION_STATES);

const correctionCommon = {
  id: idSchema("cor"),
  communication_id: idSchema("com"),
  base_revision_id: idSchema("rev"),
  reason: z.string().min(1),
  evidence: z.array(evidenceRefSchema),
  actor: actorSchema,
  lease_id: idSchema("lse"),
  protected: z.boolean(),
  saved_at: instantSchema,
  resulting_revision_id: idSchema("rev"),
  resolved_problem_ids: z.array(idSchema("rpt")),
  invalidated_check_ids: z.array(idSchema("chk")),
  audit_id: idSchema("aud"),
  state: correctionStateSchema,
};

const textValue = z.strictObject({ text: z.string() });
const attributionValue = z.strictObject({ name: z.string().min(1).nullable(), profile_id: idSchema("prf").nullable() });
const unitsValue = z.strictObject({ unit_ids: z.array(idSchema("unt")).min(1) });
const communicationsBefore = z.strictObject({ communications: z.array(idSchema("com")).min(1) });
const communicationsAfter = z.strictObject({
  communications: z.array(idSchema("com")).min(1),
  revisions: z.array(idSchema("rev")).min(1),
});
const unitTarget = z.strictObject({ unit_id: idSchema("unt") });
const communicationTarget = z.strictObject({ communication_id: idSchema("com") });

export const correctionRecordSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("word"),
    target: z.strictObject({ unit_id: idSchema("unt"), word_id: z.string().min(1) }),
    before: textValue,
    after: textValue,
    ...correctionCommon,
  }),
  z.strictObject({
    kind: z.literal("turn_text"),
    target: unitTarget,
    before: textValue,
    after: textValue,
    ...correctionCommon,
  }),
  z.strictObject({
    kind: z.literal("speaker"),
    target: unitTarget,
    before: attributionValue,
    after: attributionValue,
    ...correctionCommon,
  }),
  z.strictObject({
    kind: z.literal("profile_link"),
    target: z.strictObject({ assignment_id: idSchema("asg") }),
    before: attributionValue,
    after: attributionValue,
    ...correctionCommon,
  }),
  z.strictObject({
    kind: z.literal("timing"),
    target: unitTarget,
    before: z.strictObject({ range_ms: msTupleSchema.nullable() }),
    after: z.strictObject({ range_ms: msTupleSchema }),
    ...correctionCommon,
  }),
  z.strictObject({
    kind: z.literal("turn_split"),
    target: z.strictObject({ unit_id: idSchema("unt"), at_ms: z.number().int().nonnegative() }),
    before: unitsValue,
    after: unitsValue,
    ...correctionCommon,
  }),
  z.strictObject({
    kind: z.literal("turn_merge"),
    target: z.strictObject({ unit_ids: z.array(idSchema("unt")).min(2) }),
    before: unitsValue,
    after: unitsValue,
    ...correctionCommon,
  }),
  z.strictObject({
    kind: z.literal("communication_split"),
    target: z.strictObject({ evidence_revision_id: idSchema("rev"), at_ms: z.number().int().nonnegative() }),
    before: communicationsBefore,
    after: communicationsAfter,
    ...correctionCommon,
  }),
  z.strictObject({
    kind: z.literal("communication_merge"),
    target: z.strictObject({ communication_ids: z.array(idSchema("com")).min(2) }),
    before: communicationsBefore,
    after: communicationsAfter,
    ...correctionCommon,
  }),
  z.strictObject({
    kind: z.literal("date"),
    target: communicationTarget,
    before: z.strictObject({ occurrence_date: sourceDateSchema.nullable() }),
    after: z.strictObject({ occurrence_date: sourceDateSchema }),
    ...correctionCommon,
  }),
  z.strictObject({
    kind: z.literal("context_status"),
    target: communicationTarget,
    before: z.strictObject({ context_status: contextStatusSchema }),
    after: z.strictObject({ context_status: contextStatusSchema }),
    ...correctionCommon,
  }),
  z.strictObject({
    kind: z.literal("supporting_evidence"),
    target: z.union([unitTarget, communicationTarget]),
    before: z.strictObject({ evidence: z.array(evidenceRefSchema) }),
    after: z.strictObject({ evidence: z.array(evidenceRefSchema) }),
    ...correctionCommon,
  }),
]);
export type CorrectionRecord = z.infer<typeof correctionRecordSchema>;

export const CARRY_OVER_STATES = [
  "applied",
  "pending",
  "deferred",
  "reverted",
  "applied_by_editor",
  "dismissed",
] as const;
export const carryOverStateSchema = z.enum(CARRY_OVER_STATES);

export const carryOverRecordSchema = z.strictObject({
  id: idSchema("cov"),
  correction_id: idSchema("cor"),
  target_copy_id: idSchema("cpy"),
  state: carryOverStateSchema,
  alignment_id: idSchema("aln").nullable(),
  condition: z.string().min(1),
  note: z.string().min(1).nullable(),
  decided_by: actorSchema,
  at: instantSchema,
});
export type CarryOverRecord = z.infer<typeof carryOverRecordSchema>;

export const EDIT_LEASE_STATES = ["held", "released", "expired"] as const;
export const editLeaseStateSchema = z.enum(EDIT_LEASE_STATES);

export const editLeaseRecordSchema = z
  .strictObject({
    id: idSchema("lse"),
    communication_id: idSchema("com"),
    evidence_revision_id: idSchema("rev"),
    holder: idSchema("usr"),
    session_ref: z.string().min(1),
    state: editLeaseStateSchema,
    acquired_at: instantSchema,
    last_heartbeat_at: instantSchema,
    expires_at: instantSchema,
    released_at: instantSchema.optional(),
  })
  .refine((l) => (l.state === "released") === (l.released_at !== undefined), "released_at is set exactly when released")
  .refine(
    (l) => l.acquired_at <= l.last_heartbeat_at && l.last_heartbeat_at < l.expires_at,
    "lease times are out of order",
  );
export type EditLeaseRecord = z.infer<typeof editLeaseRecordSchema>;

export const REPORTED_PROBLEM_KINDS = ["wrong_text", "wrong_speaker", "wrong_timing", "context", "other"] as const;
export const reportedProblemKindSchema = z.enum(REPORTED_PROBLEM_KINDS);
export const REPORTED_PROBLEM_STATES = ["open", "resolved"] as const;
export const reportedProblemStateSchema = z.enum(REPORTED_PROBLEM_STATES);
export const REPORTED_PROBLEM_SCOPES = ["item", "communication", "passage"] as const;

export const reportedProblemRecordSchema = z
  .strictObject({
    id: idSchema("rpt"),
    scope: z.discriminatedUnion("kind", [
      z.strictObject({ kind: z.literal("item"), item_id: idSchema("itm") }),
      z.strictObject({ kind: z.literal("communication"), communication_id: idSchema("com") }),
      z.strictObject({
        kind: z.literal("passage"),
        communication_id: idSchema("com"),
        unit_ids: z.array(idSchema("unt")).min(1),
      }),
    ]),
    kind: reportedProblemKindSchema,
    description: z.string().min(1),
    state: reportedProblemStateSchema,
    reported_by: actorSchema,
    reported_at: instantSchema,
    task_id: idSchema("tsk"),
    resolution: z
      .discriminatedUnion("kind", [
        z.strictObject({
          kind: z.literal("correction"),
          by: actorSchema,
          at: instantSchema,
          correction_id: idSchema("cor"),
          note: z.string().min(1).nullable(),
        }),
        z.strictObject({
          kind: z.literal("note"),
          by: actorSchema,
          at: instantSchema,
          correction_id: z.null(),
          note: z.string().min(1),
        }),
      ])
      .nullable(),
  })
  .refine((p) => (p.state === "resolved") === (p.resolution !== null), "resolution is set exactly when resolved");
export type ReportedProblemRecord = z.infer<typeof reportedProblemRecordSchema>;
